import { useEffect, useRef } from 'react';

import { refreshAuthToken } from '~/api/refresh-token';
import { useAuthStore } from '~/store/auth.store';

import { createExportStatusStream, ExportStreamError } from '../services/export-status-stream';
import { getExportStatus } from '../services/export.service';
import type { ExportStatusResponse } from '../types/export.types';
import { isTerminalExportStatus } from '../types/export.types';

interface Options {
  exportId: string;
  /** Called for each status frame (SSE or the fallback poll). */
  onStatus: (status: ExportStatusResponse) => void;
  /**
   * Fired once when the stream exhausts its reconnect budget and even the
   * fallback poll couldn't reach a terminal state. Consumers surface this
   * (e.g. flip the toast to an error) so a dead stream doesn't hang forever.
   */
  onUnavailable?: () => void;
}

/** Reconnect backoff: 1s, 2s, 4s, 8s, 16s (capped). */
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 16_000;
const MAX_RECONNECT_ATTEMPTS = 5;

/** Duck-typed guard — survives module-graph duplication where `instanceof` can fail. */
function statusOf(err: unknown): number | undefined {
  return (err as { status?: number }).status;
}
function isAuthError(err: unknown): boolean {
  const s = statusOf(err);
  return s === 401 || s === 403;
}
function retryAfterOf(err: unknown): number | undefined {
  return err instanceof ExportStreamError ? err.retryAfterSeconds : undefined;
}

/** ±50% jitter — prevents synchronized reconnect stampedes. */
function jitter(baseMs: number): number {
  return baseMs * (0.5 + Math.random());
}

/**
 * Owns the SSE lifecycle for a single export's status stream:
 *   - Opens when authenticated; reconnects with capped exponential backoff on
 *     drops; one-shot token refresh on 401/403 (the raw-fetch stream isn't
 *     covered by the Ky auth interceptor).
 *   - Stops permanently once a terminal frame arrives or the server closes the
 *     stream after delivering one (COMPLETED/FAILED/CANCELLED never reconnect).
 *   - On exhausting the reconnect budget, does one final `GET /{exportId}` poll
 *     to capture a terminal state; if still non-terminal, fires `onUnavailable`.
 *
 * `onStatus`/`onUnavailable` are captured via refs so a parent re-render doesn't
 * tear the stream down.
 */
export function useExportStatusStream({ exportId, onStatus, onUnavailable }: Options): void {
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));

  const statusRef = useRef(onStatus);
  const unavailableRef = useRef(onUnavailable);

  useEffect(() => {
    statusRef.current = onStatus;
  }, [onStatus]);
  useEffect(() => {
    unavailableRef.current = onUnavailable;
  }, [onUnavailable]);

  useEffect(() => {
    if (!isAuthed || !exportId) return;

    let cancelled = false;
    let attempts = 0;
    let terminalSeen = false;
    let authRefreshed = false;
    let controller: AbortController | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    /** Last-ditch single read so a dead stream still captures a finished export. */
    function fallbackPoll(): void {
      void getExportStatus(exportId)
        .then((dto) => {
          if (cancelled) return;
          statusRef.current(dto);
          if (!isTerminalExportStatus(dto.status)) unavailableRef.current?.();
        })
        .catch(() => {
          if (!cancelled) unavailableRef.current?.();
        });
    }

    function scheduleReconnect(retryAfterSeconds?: number): void {
      if (cancelled || terminalSeen) return;
      if (attempts >= MAX_RECONNECT_ATTEMPTS) {
        fallbackPoll();
        return;
      }
      attempts += 1;
      const base = Math.min(
        MAX_RECONNECT_DELAY_MS,
        BASE_RECONNECT_DELAY_MS * 2 ** Math.min(attempts - 1, 4),
      );
      const delay = retryAfterSeconds !== undefined ? retryAfterSeconds * 1000 : jitter(base);
      reconnectTimeout = setTimeout(connect, delay);
    }

    function handleError(error: Error): void {
      if (cancelled || terminalSeen) return;
      // Expired/again-rejected token (gateway uses 401 or 403): refresh once and
      // reconnect with the new token — the raw-fetch stream isn't refreshed by
      // the Ky interceptor, so nothing else would.
      if (isAuthError(error) && !authRefreshed) {
        authRefreshed = true;
        void refreshAuthToken().then((outcome) => {
          if (cancelled) return;
          if (outcome === 'refreshed') connect();
          else if (outcome === 'transient') scheduleReconnect();
          // 'invalid' → tokens cleared upstream; the Ky interceptor owns the
          // /login bounce on the next request. Don't reconnect.
        });
        return;
      }
      scheduleReconnect(retryAfterOf(error));
    }

    function connect(): void {
      if (cancelled || terminalSeen) return;
      controller = createExportStatusStream({
        exportId,
        onOpen: () => {
          attempts = 0;
          authRefreshed = false;
        },
        onStatus: (dto) => {
          statusRef.current(dto);
          if (isTerminalExportStatus(dto.status)) {
            terminalSeen = true;
            controller?.abort();
          }
        },
        onComplete: () => {
          // Server auto-closes after the first terminal frame. A clean close
          // WITHOUT a terminal frame means the stream dropped mid-flight —
          // reconnect (the server sends a fresh frame within ~2s).
          if (!terminalSeen) scheduleReconnect();
        },
        onError: handleError,
      });
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      controller?.abort();
    };
  }, [exportId, isAuthed]);
}
